// The Stats tab on a question's View page (founder 2026-11-04, "Yes to all
// 6, build them"): how members answered a Daily Question, evening by
// evening, counts only. The words and the counting rule are in
// questionStatsUtils. Mounted on the first click of the tab, so the numbers
// load then and never on the page's own load; it stays mounted after, so a
// second visit to the tab does not read them again.
import React, { useCallback, useEffect, useState } from "react";
import { getVaultQuestionStats } from "../services/vaultQuestionService";
import {
  STATS_INTRO,
  REVIEW_CITY_TOTAL_NOTE,
  answeredLabel,
  askerSeesNote,
  barsOf,
  emptyLine,
  leaderPill,
  moreEveningsNote,
  mostChosen,
  noAnswersLine,
  resultsLine,
  runHeader,
  runStatePill,
  runTestNote,
  soonLine,
  statsFootnote,
  totalHeader,
  totalTestNote,
} from "./questionStatsUtils";

const NOTE = "text-xs text-secondary-light";
const TABULAR = { fontVariantNumeric: "tabular-nums" };
const MINI_PILL = { borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" };

const Tile = ({ label, value, sub, small }) => (
  <div className="col-md-6 mb-12">
    <div className="p-16 radius-8 bg-neutral-50 h-100">
      <div className="text-sm text-secondary-light mb-1">{label}</div>
      <h5 className={`mb-0 fw-semibold ${small ? "text-md" : ""}`} style={TABULAR}>
        {value}
      </h5>
      {sub ? <div className={`${NOTE} mt-1`} style={TABULAR}>{sub}</div> : null}
    </div>
  </div>
);

// One bar per answer, in the answers' own order. The leader is lilac-600,
// the rest lilac-300 (Curiouzz colours, never green).
const Bars = ({ block, options }) => (
  <div className="mt-4">
    {barsOf(block, options).map((bar, i) => (
      <div key={i} className="d-flex align-items-center gap-12 mb-8 flex-wrap flex-md-nowrap" data-testid="stats-bar">
        <div className="text-sm text-primary-light d-flex align-items-center gap-8 flex-wrap" style={{ width: 260, maxWidth: "100%" }}>
          <span>{bar.label}</span>
          {bar.leader ? (
            <span className="bg-lilac-100 text-lilac-600 text-xs fw-medium" style={MINI_PILL}>
              {leaderPill(block)}
            </span>
          ) : null}
        </div>
        <div className="bg-neutral-200 flex-grow-1" style={{ height: 10, borderRadius: 999, minWidth: 80, overflow: "hidden" }}>
          <div
            className={bar.leader ? "bg-lilac-600" : "bg-lilac-300"}
            style={{ width: `${bar.pct}%`, height: "100%", borderRadius: 999 }}
          />
        </div>
        <div className="text-sm text-secondary-light text-end" style={{ ...TABULAR, width: 80 }}>
          {bar.count} · {bar.pct}%
        </div>
      </div>
    ))}
  </div>
);

// The numbers of one evening, or of every evening together.
const Numbers = ({ block, options, open }) => {
  if (!(block && block.answered > 0)) {
    return <div className="text-sm text-secondary-light">{noAnswersLine(open)}</div>;
  }
  const top = mostChosen(block, options);
  return (
    <>
      <div className="row">
        <Tile label={answeredLabel(open)} value={block.answered} />
        {top ? <Tile label="Most chosen" value={top.value} sub={top.sub} small /> : null}
      </div>
      <Bars block={block} options={options} />
    </>
  );
};

const Block = ({ header, pill, children }) => (
  <div className="radius-8 p-20 mb-16" style={{ border: "1px solid var(--neutral-200)" }} data-testid="stats-block">
    <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap mb-12">
      <h6 className="text-md fw-semibold mb-0">{header}</h6>
      {pill ? (
        <span
          className={`text-xs fw-semibold ${pill.open ? "bg-lilac-100 text-lilac-600 border border-lilac-600" : "bg-neutral-200 text-secondary-light"}`}
          style={{ ...MINI_PILL, padding: "3px 10px" }}
        >
          {pill.text}
        </span>
      ) : null}
    </div>
    {children}
  </div>
);

const QuestionStats = ({ questionId }) => {
  const [state, setState] = useState({ loading: true, error: false, data: null });

  const load = useCallback(async () => {
    setState({ loading: true, error: false, data: null });
    try {
      const res = await getVaultQuestionStats(questionId);
      const data = res && res.data && res.data.data;
      if (!data || (res.data.success === false)) throw new Error("no data");
      setState({ loading: false, error: false, data });
    } catch (err) {
      setState({ loading: false, error: true, data: null });
    }
  }, [questionId]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading) return <div className="text-sm text-secondary-light">Loading the numbers...</div>;

  if (state.error) {
    return (
      <div className="d-flex align-items-center gap-12 flex-wrap">
        <span className="text-sm text-secondary-light">Could not load the numbers.</span>
        <button
          type="button"
          className="btn btn-sm radius-8 text-lilac-600 border border-lilac-600 bg-transparent px-12 py-6"
          onClick={load}
        >
          Try again
        </button>
      </div>
    );
  }

  const data = state.data;
  if (data.applies === false) {
    return <div className="text-sm text-secondary-light">Stats are kept for Daily Questions only.</div>;
  }
  const options = Array.isArray(data.options) ? data.options : [];
  const runs = Array.isArray(data.runs) ? data.runs : [];
  const dropHour = data.dropHour;
  const member = data.member || null;
  const firstName = member && member.firstName;

  if (!runs.length) return <div className="text-sm text-secondary-light">{emptyLine(data, dropHour)}</div>;

  // Dealt at 7 PM and nothing shown yet: one line per city, nothing else.
  if (runs.every((r) => r && r.state === "tonight")) {
    return (
      <div>
        {runs.map((r, i) => (
          <div key={i} className="text-sm text-secondary-light mb-1">
            {soonLine(r, dropHour, { withCity: true })}
          </div>
        ))}
      </div>
    );
  }

  const total = data.total || null;
  const totalTests = totalTestNote(total);
  // Past the newest 60 evenings (2026-11-04): said in the total block, or
  // under the intro when there is no total.
  const more = moreEveningsNote(data, runs.length);
  return (
    <div>
      <p className={`text-sm text-secondary-light ${more && !total ? "mb-8" : "mb-16"}`}>{STATS_INTRO}</p>
      {more && !total ? <p className={`${NOTE} mb-16`}>{more}</p> : null}

      {total ? (
        <Block header={totalHeader(total)}>
          <Numbers block={total} options={options} open={total.open === true} />
          <div className="mt-8">
            {totalTests ? <div className={NOTE}>{totalTests}</div> : null}
            {runs.some((r) => r && r.reviewCity) ? <div className={NOTE}>{REVIEW_CITY_TOTAL_NOTE}</div> : null}
            {more ? <div className={NOTE}>{more}</div> : null}
          </div>
        </Block>
      ) : null}

      {runs.map((run, i) => {
        const tonight = run.state === "tonight";
        const open = run.state === "open";
        const results = resultsLine(run, { member, firstName });
        return (
          <Block key={`${run.pocketId || i}-${run.dateKey}`} header={runHeader(run)} pill={runStatePill(run, dropHour)}>
            {tonight ? (
              <div className="text-sm text-secondary-light">{soonLine(run, dropHour)}</div>
            ) : (
              <>
                <Numbers block={run} options={options} open={open} />
                <div className="mt-8">
                  {runTestNote(run) ? <div className={NOTE}>{runTestNote(run)}</div> : null}
                  {run.askerSees === true ? <div className={NOTE}>{askerSeesNote(firstName)}</div> : null}
                  {results ? <div className={NOTE}>{results}</div> : null}
                </div>
              </>
            )}
          </Block>
        );
      })}

      <p className={`${NOTE} mb-0 mt-8`}>{statsFootnote(dropHour)}</p>
    </div>
  );
};

export default QuestionStats;
